import React, { useEffect, useReducer } from "react";
import {
  Table, Label, Message, Icon, Loader, Segment, Dimmer, Button, Pagination,
} from "semantic-ui-react";
import { fetchVodafoneOrders, declineVodafoneOrder } from "../../libs/requests/vodafoneOrderSearch";

const PAGE_SIZE = 10;

function statusColour(status) {
  switch (status?.toLowerCase()) {
    case "accepted":    return "green";
    case "pending":     return "yellow";
    case "in progress": return "blue";
    case "failed":      return "red";
    default:            return "grey";
  }
}

function reducer(state, action) {
  switch (action.type) {
    case "SET_DATA":    return { ...state, orders: action.orders, isLoading: false, error: null };
    case "SET_LOADING": return { ...state, isLoading: true, error: null };
    case "SET_ERROR":   return { ...state, isLoading: false, error: action.error };
    case "REMOVE":      return { ...state, orders: state.orders.filter(o => o.reference !== action.ref) };
    case "SET_PAGE":    return { ...state, page: action.page };
    default:            return state;
  }
}

export default function VodafoneOrdersTable({ statusFilter = "" }) {
  const [state, dispatch] = useReducer(reducer, { orders: [], isLoading: false, error: null, page: 1 });
  const [declining, setDeclining] = React.useState(null);
  const [actionMsg, setActionMsg] = React.useState(null);

  useEffect(() => { load(); }, [statusFilter]);

  async function load() {
    dispatch({ type: "SET_LOADING" });
    try {
      const data = await fetchVodafoneOrders("", statusFilter);
      dispatch({ type: "SET_DATA", orders: Array.isArray(data) ? data : [] });
    } catch {
      dispatch({ type: "SET_ERROR", error: "Failed to load Vodafone orders." });
    }
  }

  async function handleDecline(ref) {
    if (!window.confirm(`Decline order ${ref}? This cannot be undone.`)) return;
    setDeclining(ref);
    try {
      await declineVodafoneOrder(ref);
      dispatch({ type: "REMOVE", ref });
      setActionMsg({ positive: true, text: `Order ${ref} declined.` });
    } catch {
      setActionMsg({ positive: false, text: `Failed to decline ${ref}.` });
    } finally {
      setDeclining(null);
    }
  }

  if (state.isLoading) {
    return (
      <Segment>
        <Dimmer active inverted><Loader>Loading Vodafone orders…</Loader></Dimmer>
      </Segment>
    );
  }

  if (state.error) {
    return <Message negative><Message.Header>{state.error}</Message.Header></Message>;
  }

  if (!state.orders.length) {
    return (
      <Message info>
        <Icon name="info circle" /> No Vodafone orders found.
      </Message>
    );
  }

  const totalPages = Math.ceil(state.orders.length / PAGE_SIZE);
  const visible = state.orders.slice((state.page - 1) * PAGE_SIZE, state.page * PAGE_SIZE);

  return (
    <>
      {actionMsg && (
        <Message positive={actionMsg.positive} negative={!actionMsg.positive}
          onDismiss={() => setActionMsg(null)}>
          {actionMsg.text}
        </Message>
      )}

      <Table celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Reference</Table.HeaderCell>
            <Table.HeaderCell>Status</Table.HeaderCell>
            <Table.HeaderCell>Product</Table.HeaderCell>
            <Table.HeaderCell>Address</Table.HeaderCell>
            <Table.HeaderCell>Activation Date</Table.HeaderCell>
            <Table.HeaderCell>Updates</Table.HeaderCell>
            <Table.HeaderCell />
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {visible.map((order) => (
            <Table.Row key={order.reference}>
              <Table.Cell>{order.reference}</Table.Cell>
              <Table.Cell>
                <Label color={statusColour(order.status)}>{order.status}</Label>
              </Table.Cell>
              <Table.Cell>{order.productDescription ?? "—"}</Table.Cell>
              <Table.Cell>{order.address ?? "—"}</Table.Cell>
              <Table.Cell>
                {order.activationDate
                  ? new Date(order.activationDate).toLocaleDateString("en-GB")
                  : "—"}
              </Table.Cell>
              <Table.Cell>
                {order.updates?.length
                  ? <Label circular color="blue">{order.updates.length}</Label>
                  : "—"}
              </Table.Cell>
              <Table.Cell>
                {order.status === "Pending" && (
                  <Button negative size="mini"
                    loading={declining === order.reference}
                    onClick={() => handleDecline(order.reference)}>
                    Decline
                  </Button>
                )}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      {totalPages > 1 && (
        <Pagination activePage={state.page} totalPages={totalPages}
          onPageChange={(_, { activePage }) => dispatch({ type: "SET_PAGE", page: activePage })} />
      )}

      <Button icon labelPosition="left" onClick={load} style={{ marginTop: "8px" }}>
        <Icon name="refresh" /> Refresh
      </Button>
    </>
  );
}
